import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MenuItem, Select } from '@mui/material';
import { sanitizeHtml } from 'utils';
import { resolveRichTextImages } from '../../../utils/resolveRichTextImages';
import {
    formatInlinePhone,
    getInlineFieldErrors,
    inlineFieldErrors,
    inlineFieldTypes,
    inlineInputTypes,
    inlineSelectTypes,
    inlineFieldsToSlots,
    parseGoalBlocks,
    parseInlineFields,
    readInlineFieldValue,
    updateGoalBlock,
    updateInlineField,
    writeInlineFieldValue,
} from '../../../utils/inlineFields';
import { GoalBlockFill } from './goalBlockFill';
import { InlineChoiceField } from './inlineChoiceField';

const NATIVE_INPUT_TYPE = {
    [inlineInputTypes.DATE]: 'date',
    [inlineInputTypes.TIME]: 'time',
};

// Typed values that grow with what staff type, so neither the value nor the placeholder is ever cut off.
// Date / Time keep the native picker (fixed width, no placeholder). A Number is checked on blur
// (`getInlineFieldErrors`), like an email or phone.
const AUTOSIZE_INPUT_TYPES = [inlineInputTypes.TEXT, inlineInputTypes.EMAIL, inlineInputTypes.PHONE, inlineInputTypes.NUMBER];

const INPUT_MODE = { [inlineInputTypes.PHONE]: 'tel', [inlineInputTypes.EMAIL]: 'email', [inlineInputTypes.NUMBER]: 'decimal' };

const optionsOf = (field) => (field?.properties?.options || []).filter((opt) => opt?.name || opt?.value);

/** One inline field as a fillable control, sized to sit inside a line of text. */
export const InlineFieldControl = ({ field, value, onChange, onBlur, disabled, error }) => {
    const current = readInlineFieldValue(field, value);
    const required = field?.properties?.required;
    const isDisabled = disabled || !!field?.properties?.readOnly;
    const errorClass = error ? ' inline-field-control-error' : '';
    // The author's placeholder wins; the label is only a fallback for a field without one.
    const placeholder = field?.placeholder || field?.label || '';

    let control;
    if (field?.type === inlineFieldTypes.SELECT) {
        const multiple = field?.defaultDateType === inlineSelectTypes.MULTIPLE;
        const hasValue = multiple ? !!current?.length : !!current;
        control = (
            <Select
                size="small"
                multiple={multiple}
                displayEmpty
                disabled={isDisabled}
                value={multiple ? current : current || ''}
                onChange={(e) => onChange(e.target.value)}
                className={`inline-field-select${hasValue && !isDisabled ? ' inline-field-select-clearable' : ''}${errorClass}`}
                // × clears the pick (every pick on a multiple select); it does not open the list.
                endAdornment={
                    hasValue && !isDisabled ? (
                        <button
                            type="button"
                            className="inline-field-select-clear"
                            aria-label="Clear"
                            title="Clear"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange(multiple ? [] : '');
                            }}
                        >
                            ×
                        </button>
                    ) : null
                }
                // A fixed-width list: long options wrap onto more lines instead of stretching it across the page.
                MenuProps={{ PaperProps: { className: 'inline-field-options-menu' } }}
                renderValue={(selected) => {
                    const ids = multiple ? selected : selected ? [selected] : [];
                    if (!ids.length) return <span className="inline-field-placeholder">{placeholder || 'Select'}</span>;
                    return ids.map((optId) => optionsOf(field).find((opt) => opt.id === optId)?.name || optId).join(', ');
                }}
            >
                {optionsOf(field).map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                        {opt.name || opt.value}
                    </MenuItem>
                ))}
            </Select>
        );
    } else if (field?.type === inlineFieldTypes.CHECK_BOX || field?.type === inlineFieldTypes.RADIO_BUTTON) {
        // A chip that opens the options in a modal: a list of options would break the line of text.
        control = <InlineChoiceField field={field} value={current} disabled={isDisabled} error={error} onChange={onChange} />;
    } else if (AUTOSIZE_INPUT_TYPES.includes(field?.defaultDateType || inlineInputTypes.TEXT)) {
        // Grows with the text like the words around it: wider as staff type, then wrapping onto
        // more lines at the editor's edge. A hidden copy of the text (`data-value`, drawn by the
        // wrapper's ::after) sizes the grid cell the textarea fills.
        const isPhone = field?.defaultDateType === inlineInputTypes.PHONE;
        const hint = field?.placeholder || (isPhone ? '(000) 000-0000' : placeholder);
        control = (
            <span className="inline-field-autosize" data-value={current || hint}>
                <textarea
                    // cols / rows 1: the textarea's own default size would set the minimum instead of the CSS.
                    rows={1}
                    cols={1}
                    inputMode={INPUT_MODE[field?.defaultDateType]}
                    className={`inline-field-input${errorClass}`}
                    placeholder={hint}
                    disabled={isDisabled}
                    value={current}
                    aria-invalid={!!error}
                    // One value, not paragraphs: Enter does not break the line.
                    onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                    onChange={(e) => {
                        const next = e.target.value.replace(/\n/g, ' ');
                        onChange(isPhone ? formatInlinePhone(next) : next);
                    }}
                />
            </span>
        );
    } else {
        control = (
            <input
                type={NATIVE_INPUT_TYPE[field?.defaultDateType] || 'text'}
                className={`inline-field-input${errorClass}`}
                placeholder={placeholder}
                disabled={isDisabled}
                value={current}
                aria-invalid={!!error}
                onChange={(e) => onChange(e.target.value)}
            />
        );
    }

    return (
        <span className="inline-field-control" title={error || field?.label || ''} onBlur={onBlur}>
            {control}
            {required && <span className="inline-field-required">*</span>}
        </span>
    );
};

/**
 * Which inline field / Goal block errors in `source` to show (`getInlineFieldErrors`):
 * a value its type does not accept (email, phone, number, date, time) once
 * staff left the field (`markTouched`); a required one left empty once the note
 * was submitted (`showErrors`). `shownErrors` lists them for `InlineFieldErrorList`;
 * fields inside a Goal block's goals list theirs under their own goal.
 */
export const useInlineFieldErrors = (source, { preview, hideStaffOnly, showErrors }) => {
    const [touched, setTouched] = useState({});
    const byId = Object.fromEntries(parseInlineFields(source).map((entry) => [entry.id, entry]));
    const goalBlocks = Object.fromEntries(parseGoalBlocks(source).map((entry) => [entry.id, entry]));
    const allErrors = preview ? {} : getInlineFieldErrors(source, { skipStaffOnly: hideStaffOnly });
    const errorFor = (id) => {
        const message = allErrors[id];
        if (!message) return '';
        const waitsForSubmit = message === inlineFieldErrors.REQUIRED || message === inlineFieldErrors.GOALS;
        return showErrors || (touched[id] && !waitsForSubmit) ? message : '';
    };
    const markTouched = (id) => !touched[id] && setTouched((prev) => ({ ...prev, [id]: true }));
    const shownErrors = Object.keys(allErrors)
        .filter((id) => byId[id] || goalBlocks[id])
        .map((id) => ({ id, message: errorFor(id), label: byId[id]?.field?.label || goalBlocks[id]?.block?.label || '' }))
        .filter(({ message }) => message);
    return { errorFor, markTouched, shownErrors };
};

/** What to fix, under the text: one line per field, named by its label. */
export const InlineFieldErrorList = ({ errors }) =>
    errors.length ? (
        <ul className="inline-fields-errors">
            {errors.map(({ id, label, message }) => (
                <li key={id}>{label ? `${label}: ${message}` : message}</li>
            ))}
        </ul>
    ) : null;

/**
 * A RICH_TEXT holding inline fields, drawn read-only with each field as a
 * control in its place: the template Preview, and each picked goal's html in a
 * Goal block (`GoalBlockFill`). A note's own Rich Text is filled in the editor
 * instead (`RichTextElement` with `fill`), so staff can also type and format.
 * A Goal block is drawn as its goal picker with the picked goals (`GoalBlockFill`).
 *
 * The HTML is written once with an empty slot per field (`inlineFieldsToSlots`,
 * which does not change as values change) and each control is portaled into
 * its slot, so typing never rebuilds the text or drops focus.
 *
 * `onChange(html)` receives the text with the new value on the field's span.
 * `preview` draws disabled, empty controls (template preview).
 *
 * Errors (`getInlineFieldErrors`) outline the control and are listed under the
 * text: a value its type does not accept (email, phone, number, date, time) as
 * soon as staff leave the field; a required field left empty once the note was
 * submitted (`showErrors`).
 */
export const InlineFieldsRichText = ({ html, onChange, disabled, preview, hideStaffOnly, showErrors, imageUrls, className = '' }) => {
    const containerRef = useRef(null);
    const [slots, setSlots] = useState([]);
    const source = typeof html === 'string' ? html : '';
    const { errorFor, markTouched, shownErrors } = useInlineFieldErrors(source, { preview, hideStaffOnly, showErrors });

    const slotHtml = inlineFieldsToSlots(source);
    const structure = useMemo(() => sanitizeHtml(resolveRichTextImages(slotHtml, imageUrls || {})), [slotHtml, imageUrls]);

    // The markup is written here rather than through dangerouslySetInnerHTML: React would
    // re-apply that on the re-render that mounts the controls, replacing the slot nodes they
    // were portaled into. The container has no React children, so React never touches it.
    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        container.innerHTML = structure;
        const nodes = container.querySelectorAll('[data-inline-slot]');
        setSlots(Array.from(nodes).map((el) => ({ id: el.getAttribute('data-inline-slot'), el })));
    }, [structure]);

    const byId = Object.fromEntries(parseInlineFields(source).map((entry) => [entry.id, entry]));
    const goalBlocks = Object.fromEntries(parseGoalBlocks(source).map((entry) => [entry.id, entry]));

    return (
        <>
            <div ref={containerRef} className={`richtext-readonly-content inline-fields-text ${className}`} />
            {slots.map(({ id, el }) => {
                const blockEntry = goalBlocks[id];
                if (blockEntry && el.isConnected) {
                    return createPortal(
                        <GoalBlockFill
                            block={blockEntry.block}
                            value={preview ? { goals: [] } : blockEntry.value}
                            disabled={disabled || preview}
                            error={!!errorFor(id)}
                            showErrors={showErrors}
                            hideStaffOnly={hideStaffOnly}
                            imageUrls={imageUrls}
                            onChange={(next) => onChange?.(updateGoalBlock(source, id, { value: next }))}
                        />,
                        el,
                        id,
                    );
                }
                const entry = byId[id];
                if (!entry || !el.isConnected) return null;
                if (hideStaffOnly && entry.field?.properties?.staffOnly) return null;
                return createPortal(
                    <InlineFieldControl
                        field={entry.field}
                        value={preview ? '' : entry.value}
                        disabled={disabled || preview}
                        error={errorFor(id)}
                        onBlur={() => markTouched(id)}
                        onChange={(next) => onChange?.(updateInlineField(source, id, { value: writeInlineFieldValue(entry.field, next) }))}
                    />,
                    el,
                    id,
                );
            })}
            <InlineFieldErrorList errors={shownErrors} />
        </>
    );
};
